"use client";

import React, { useRef } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, TrendingUp, Users, Award } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const timelineEvents = [{
  year: "1978",
  title: "The Vision Takes Root",
  description: "Founded by a group of visionary leaders, the Colorado Hispanic Chamber of Commerce began with a singular mission: to give a voice to the growing Hispanic business community. In a small office in Denver, the seeds of economic advocacy were sown.",
  quote: "We didn't just build a chamber; we built a movement.",
  icon: Clock
}, {
  year: "1990",
  title: "Building Bridges",
  description: "As Colorado's economy shifted, so did our reach. The 90s marked a decade of strategic partnerships, bridging the gap between Hispanic entrepreneurs and major corporate players. Advocacy became our engine for growth.",
  quote: "Access to opportunity is the civil rights issue of our time.",
  icon: Users
}, {
  year: "2010",
  title: "A New Era of Influence",
  description: "With the digital revolution, COHCC embraced data-driven advocacy. We launched new leadership programs and expanded our policy influence, ensuring Hispanic businesses were at the table where decisions were made.",
  quote: "Data is our most powerful tool for equity.",
  icon: TrendingUp
}, {
  year: "2024",
  title: "Unstoppable Momentum",
  description: "Today, we stand as a pillar of the U.S. economy. With over 120,000 Hispanic-owned businesses in Colorado and $40B+ in state purchasing power, our legacy is one of undeniable impact and future-forward innovation.",
  quote: "The future of Colorado's economy is Hispanic.",
  icon: Award
}];
interface TimelineItemProps {
  event: (typeof timelineEvents)[0];
  index: number;
  isLast: boolean;
}
const TimelineItem = ({
  event,
  index,
  isLast
}: TimelineItemProps) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.5,
    once: false
  });
  return <div ref={ref as React.RefObject<HTMLDivElement>} className={`relative pl-8 md:pl-12 py-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-20 translate-y-8"}`}>
      {}
      {!isLast && <div className="absolute left-[11px] md:left-[15px] top-24 bottom-0 w-px bg-border/30" />}

      {}
      <div className={`absolute left-0 top-14 flex h-6 w-6 md:h-8 md:w-8 items-center justify-center rounded-full border-2 transition-colors duration-500 ${isVisible ? "border-primary bg-primary text-primary-foreground shadow-[0_0_15px_rgba(234,179,8,0.5)]" : "border-muted-foreground/30 bg-background text-muted-foreground"}`}>
        <div className={`h-2 w-2 rounded-full ${isVisible ? "bg-primary-foreground" : "bg-transparent"}`} />
      </div>

      {}
      <div className="flex flex-col gap-4">
        <span className={`font-mono text-4xl md:text-6xl font-bold tracking-tighter transition-colors duration-500 ${isVisible ? "text-primary" : "text-muted-foreground/20"}`}>
          {event.year}
        </span>
        
        <h3 className="text-2xl md:text-3xl font-serif font-semibold text-foreground">
          {event.title}
        </h3>
        
        <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
          {event.description}
        </p>

        <blockquote className="border-l-2 border-primary/50 pl-4 italic text-foreground/80 font-serif text-lg my-2">
          "{event.quote}"
        </blockquote>
      </div>
    </div>;
};
export default function OurLegacyOfImpact() {
  return <section id="our-legacy-of-impact" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          
          {}
          <div className="relative lg:h-[calc(100vh-12rem)] lg:sticky lg:top-24 flex flex-col justify-center">
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-sm shadow-2xl">
              {}
              <div className="absolute inset-0 z-10 pointer-events-none opacity-20 mix-blend-overlay bg-[url('https://grainy-gradients.vercel.app/noise.svg')]"></div>
              
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/founding_members_1978.png" alt="Founding members of the Colorado Hispanic Chamber of Commerce in 1978" className="h-full w-full object-cover grayscale contrast-125 brightness-90 transition-transform duration-1000 hover:scale-105" />
              
              {}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-8 z-20">
                <p className="text-white/90 font-serif italic text-lg">
                  "The foundation of our future was built on the courage of our past."
                </p>
                <p className="text-primary text-sm font-mono mt-2 uppercase tracking-widest">
                  Est. 1978 • Denver, CO
                </p>
              </div>
            </div>

            {}
            <div className="absolute -z-10 -top-12 -left-12 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
            <div className="absolute -z-10 -bottom-12 -right-12 w-64 h-64 bg-secondary/10 rounded-full blur-3xl"></div>
          </div>

          {}
          <div className="relative pt-8 lg:pt-0">
            <div className="mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-serif mb-6 tracking-tight">
                A Legacy of <span className="text-primary">Impact</span>
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed">
                From a grassroots initiative to a powerhouse of economic development, our journey reflects the resilience and ambition of the Hispanic community in Colorado.
              </p>
            </div>

            <div className="flex flex-col">
              {timelineEvents.map((event, i) => <div key={event.year} data-index={i}>
                  <TimelineItem event={event} index={i} isLast={i === timelineEvents.length - 1} />
                </div>)}
            </div>

            <div className="mt-16 pl-8 md:pl-12">
              <Button asChild size="lg" className="text-lg px-8 h-14 rounded-full">
                <Link to="/JoinUsPage">
                  Be Part of History <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}